import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { ArrowClockwise, Eye } from 'phosphor-react';
import { EmailDelivery, EmailDeliveryStatus } from '@dill/dill-shared';
import { DillButton, DillColumn, DillTable } from '../../../general/Dill';
import { useAppDispatch } from '../../../../redux/store';
import { getSupplierEmailDeliveries } from '../../../../redux/services/supplierBuyersService';
import { InvoiceNoticeLog } from '../../../../utils/interfaces/invoiceNoticeLogInterfaces';
import { DeliveryTimelinePanel, TimelineStep } from './DeliveryTimelinePanel';

// NOT_TRACKED: an invoice email sent before delivery tracking, known only from the notice log.
type RowStatus = EmailDeliveryStatus | 'NOT_TRACKED';

export type EmailRow = {
  id: string;
  sentAt: Date | null;
  customerName: string;
  documentType: 'INVOICE' | 'STATEMENT';
  documentTypeLabel: string;
  documentName: string;
  recipientEmail: string;
  status: RowStatus;
  statusLabel: string;
  statusDetail: string | null;
  deliveredAt: Date | null;
  failedAt: Date | null;
  opens: Date[];
};

export const EMAIL_STATUS_STYLES: Record<RowStatus, { label: string; className: string }> = {
  SENT: { label: 'Sent', className: 'bg-blue-50 text-blue-700' },
  DEFERRED: { label: 'Delayed', className: 'bg-amber-50 text-amber-700' },
  DELIVERED: { label: 'Delivered', className: 'bg-green-100 text-green-800' },
  BOUNCED: { label: 'Bounced', className: 'bg-red-50 text-red-700' },
  DROPPED: { label: 'Not sent (dropped)', className: 'bg-red-50 text-red-700' },
  BLOCKED: { label: 'Blocked', className: 'bg-red-50 text-red-700' },
  FAILED: { label: 'Failed to send', className: 'bg-red-50 text-red-700' },
  NOT_TRACKED: { label: 'Sent (not tracked)', className: 'bg-gray-100 text-gray-600' },
};

export const EMAIL_FAILED_STATUSES: RowStatus[] = ['BOUNCED', 'DROPPED', 'BLOCKED', 'FAILED'];

const toDate = (value?: Date | string | null) => (value ? new Date(value) : null);

const noticeLogSentAt = (sentAt: InvoiceNoticeLog['sentAt']): Date | null => {
  if (!sentAt) return null;
  if (typeof sentAt === 'object' && '_seconds' in sentAt) return new Date(sentAt._seconds * 1000);
  return new Date(sentAt as string);
};

export const toEmailRow = (delivery: EmailDelivery): EmailRow => ({
  id: delivery.id,
  sentAt: toDate(delivery.sentAt),
  customerName: delivery.buyerName || '',
  documentType: delivery.documentType,
  documentTypeLabel: delivery.documentType === 'INVOICE' ? 'Invoice' : 'Statement',
  documentName: delivery.documentLabel || delivery.documentId,
  recipientEmail: delivery.recipientEmail,
  status: delivery.status,
  statusLabel: EMAIL_STATUS_STYLES[delivery.status].label,
  statusDetail: delivery.statusDetail ?? null,
  deliveredAt: toDate(delivery.deliveredAt),
  failedAt: toDate(delivery.failedAt),
  opens: (delivery.opens ?? [])
    .map((open) => new Date(open.at))
    .sort((a, b) => a.getTime() - b.getTime()),
});

// Invoice emails from before tracking, one row per recipient. Skipped for invoices that already
// have a tracked email, which the notice log also covers.
const toLegacyRows = (logs: InvoiceNoticeLog[], trackedInvoiceIds: Set<string>): EmailRow[] =>
  logs
    .filter((log) => !trackedInvoiceIds.has(log.invoiceId))
    .flatMap((log) =>
      (log.recipientEmails ?? []).map((recipientEmail) => ({
        id: `legacy_${log.id}_${recipientEmail}`,
        sentAt: noticeLogSentAt(log.sentAt),
        customerName: log.buyerName || '',
        documentType: 'INVOICE' as const,
        documentTypeLabel: 'Invoice',
        documentName: `Invoice ${log.invoiceId}`,
        recipientEmail,
        status: 'NOT_TRACKED' as const,
        statusLabel: EMAIL_STATUS_STYLES.NOT_TRACKED.label,
        statusDetail: null,
        deliveredAt: null,
        failedAt: null,
        opens: [],
      }))
    );

export const buildEmailSteps = (row: EmailRow): TimelineStep[] => {
  const steps: TimelineStep[] = [{ label: 'Sent', state: 'done', at: row.sentAt }];
  if (row.status === 'NOT_TRACKED') {
    return [
      ...steps,
      {
        label: 'Delivery not tracked',
        state: 'pending',
        detail: 'Sent before delivery tracking was turned on.',
      },
    ];
  }
  if (EMAIL_FAILED_STATUSES.includes(row.status)) {
    steps.push({
      label: EMAIL_STATUS_STYLES[row.status].label,
      state: 'error',
      at: row.failedAt,
      detail: row.statusDetail,
    });
  } else if (row.deliveredAt || row.status === 'DELIVERED') {
    steps.push({ label: 'Delivered', state: 'done', at: row.deliveredAt });
  } else {
    steps.push({
      label: row.status === 'DEFERRED' ? 'Delivery delayed - retrying' : 'Delivered',
      state: 'pending',
      detail: row.status === 'DEFERRED' ? row.statusDetail : null,
    });
  }
  steps.push(
    row.opens.length > 0
      ? {
          label: `Viewed ${row.opens.length} time${row.opens.length === 1 ? '' : 's'}`,
          state: 'done',
          times: row.opens,
        }
      : { label: 'Viewed', state: 'pending' }
  );
  return steps;
};

function SupplierEmailDeliveries({
  supplierId,
  legacyNoticeLogs,
  isLegacyLoading,
}: {
  supplierId: string;
  legacyNoticeLogs: InvoiceNoticeLog[];
  isLegacyLoading?: boolean;
}) {
  const dispatch = useAppDispatch();
  const [deliveries, setDeliveries] = useState<EmailDelivery[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EmailRow | null>(null);

  const fetchDeliveries = useCallback(async () => {
    if (!supplierId) return;
    setIsLoading(true);
    try {
      const result = await dispatch(getSupplierEmailDeliveries({ supplierId }));
      const payload = result.payload as { data?: EmailDelivery[] } | null;
      setDeliveries(payload?.data ?? []);
    } finally {
      setIsLoading(false);
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const rows = useMemo<EmailRow[]>(() => {
    const trackedInvoiceIds = new Set(
      deliveries
        .filter((delivery) => delivery.documentType === 'INVOICE')
        // A consolidated email covers several invoices.
        .flatMap((delivery) => delivery.documentIds ?? [delivery.documentId])
    );
    return [...deliveries.map(toEmailRow), ...toLegacyRows(legacyNoticeLogs, trackedInvoiceIds)];
  }, [deliveries, legacyNoticeLogs]);

  const columns = useMemo<DillColumn<EmailRow>[]>(
    () => [
      {
        key: 'sentAt',
        label: 'Sent',
        width: 130,
        sortable: true,
        filterable: true,
        filterType: 'dateRange',
        getSortValue: (row) => row.sentAt?.getTime() ?? 0,
        render: (_: any, row) => (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">
              {row.sentAt ? moment(row.sentAt).format('MMM D, YYYY') : '-'}
            </span>
            <span className="text-xs text-gray-500">
              {row.sentAt ? moment(row.sentAt).format('h:mm A') : ''}
            </span>
          </div>
        ),
      },
      {
        key: 'customerName',
        label: 'Customer',
        width: 200,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.customerName,
        render: (_: any, row) => (
          <span
            className="text-sm text-gray-900 line-clamp-2 break-words min-w-0"
            title={row.customerName}>
            {row.customerName || '-'}
          </span>
        ),
      },
      {
        key: 'documentName',
        label: 'Document',
        width: 190,
        sortable: true,
        getSearchValue: (row) => row.documentName,
        render: (_: any, row) => (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate">{row.documentName}</span>
            <span className="text-xs text-gray-500">
              {row.documentType === 'INVOICE' ? 'Invoice' : 'Statement'}
            </span>
          </div>
        ),
      },
      {
        key: 'documentTypeLabel',
        label: 'Type',
        width: 110,
        sortable: true,
        filterable: true,
        render: (_: any, row) => (
          <span className="text-sm text-gray-700">{row.documentTypeLabel}</span>
        ),
      },
      {
        key: 'recipientEmail',
        label: 'Recipient',
        width: 240,
        sortable: true,
        getSearchValue: (row) => row.recipientEmail,
        render: (_: any, row) => (
          <span className="text-sm text-gray-700 truncate" title={row.recipientEmail}>
            {row.recipientEmail}
          </span>
        ),
      },
      {
        key: 'statusLabel',
        label: 'Status',
        width: 230,
        sortable: true,
        filterable: true,
        render: (_: any, row) => (
          <div className="flex flex-col items-start gap-1 min-w-0">
            <span
              className={twMerge(
                'px-2 py-1 rounded-full text-xs font-medium',
                EMAIL_STATUS_STYLES[row.status].className
              )}>
              {row.statusLabel}
            </span>
            {EMAIL_FAILED_STATUSES.includes(row.status) && row.statusDetail && (
              <span className="text-xs text-red-600 truncate max-w-full" title={row.statusDetail}>
                {row.statusDetail}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'opens',
        label: 'Views',
        width: 170,
        sortable: true,
        getSortValue: (row) => row.opens.length,
        render: (_: any, row) =>
          row.opens.length > 0 ? (
            <div className="flex flex-col">
              <span className="inline-flex items-center gap-1 text-sm text-gray-900">
                <Eye size={14} />
                {row.opens.length}
              </span>
              <span className="text-xs text-gray-500">
                Last {moment(row.opens[row.opens.length - 1]).format('MMM D, h:mm A')}
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">
              {row.status === 'NOT_TRACKED' ? '-' : 'Not viewed'}
            </span>
          ),
      },
    ],
    []
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <DillTable
          columns={columns}
          data={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => setSelectedRow(row)}
          search
          searchPlaceholder="Search customers, documents or emails"
          sortable
          defaultSort={{ key: 'sentAt', dir: 'desc' }}
          loading={isLoading || isLegacyLoading}
          rowHeight={64}
          striped={false}
          resizable
          showActiveFilters
          countLabel="Emails"
          emptyMessage="No invoice or statement emails have been sent yet."
          toolbarRight={
            <DillButton
              label="Refresh"
              variant="secondary"
              icon={<ArrowClockwise size={14} />}
              onClick={fetchDeliveries}
              isDisabled={isLoading}
              height={31}
            />
          }
        />
      </div>
      {selectedRow && (
        <DeliveryTimelinePanel
          title={selectedRow.documentName}
          subtitle={`Email · ${EMAIL_STATUS_STYLES[selectedRow.status].label}`}
          facts={[
            { label: 'Customer', value: selectedRow.customerName },
            { label: 'Sent to', value: selectedRow.recipientEmail },
          ]}
          steps={buildEmailSteps(selectedRow)}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </div>
  );
}

export default SupplierEmailDeliveries;
